import {
  List,
  Datagrid,
  DateField,
  FunctionField,
  useRecordContext,
  TextField,
  ReferenceField
} from 'react-admin';
import { Box, Typography } from '@mui/material';
import { StopTimerButton } from '../../components/StopTimerButton';
import { formatDurationSeconds } from '../../utils/formatDurationSeconds';
import { Timer as TimerIcon } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';

const DurationField = ({ record }: { record?: any }) => {
  const [elapsedTime, setElapsedTime] = useState<string>('-');
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    if (!record?.startedAt) {
      setElapsedTime('-');
      setIsRunning(false);
      return;
    }

    const start = new Date(record.startedAt);
    if (isNaN(start.getTime())) {
      setElapsedTime('-');
      setIsRunning(false);
      return;
    }

    // Check if timer is running (endedAt is null)
    if (!record?.endedAt) {
      setIsRunning(true);

      const updateElapsedTime = () => {
        const now = new Date();
        const diffMs = now.getTime() - start.getTime();

        if (diffMs < 0) {
          setElapsedTime('-');
          return;
        }

        const totalSeconds = Math.floor(diffMs / 1000);
        setElapsedTime(formatDurationSeconds(totalSeconds));
      };

      // Update immediately
      updateElapsedTime();

      // Update every second
      const interval = setInterval(updateElapsedTime, 1000);

      return () => clearInterval(interval);
    } else {
      // Timer has ended, calculate final duration
      setIsRunning(false);
      const end = new Date(record.endedAt);

      if (isNaN(end.getTime())) {
        setElapsedTime('-');
        return;
      }

      const diffMs = end.getTime() - start.getTime();
      if (diffMs < 0) {
        setElapsedTime('-');
        return;
      }

      const totalSeconds = Math.floor(diffMs / 1000);
      setElapsedTime(formatDurationSeconds(totalSeconds));
    }
  }, [record?.startedAt, record?.endedAt]);

  if (elapsedTime === '-') {
    return <span>-</span>;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {isRunning && <TimerIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
      <span>{elapsedTime}</span>
    </Box>
  );
};

export const UserTimeTrackingList = () => {
  const record = useRecordContext();

  if (!record?.id) {
    return null;
  }

  return (
    <Box>
      <List
        resource="timetracking/segments"
        filter={{ userId: record.id }}
        perPage={10}
        sort={{ field: 'createdAt', order: 'DESC' }}
        sx={{
          '& .RaList-main': { mt: 0 },
          '& .RaList-actions': { display: 'none' }
        }}
        exporter={false}
        actions={false}
        disableSyncWithLocation
        storeKey={false}
        empty={
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Typography color="text.secondary" textAlign="center" py={4}>
              No time tracking segments found for this user.
            </Typography>
          </Box>
        }
      >
        <Datagrid bulkActionButtons={false}>
          <DateField source="startedAt" label="Start Time" showTime />
          <DateField source="endedAt" label="End Time" showTime />
          <FunctionField
            label="Duration"
            render={(record: any) => <DurationField record={record} />}
          />
          <FunctionField
            label="Actions"
            render={(record: any) => {
              // Only show stop button if timer is running (endedAt is null)
              if (!record?.endedAt) {
                return <StopTimerButton record={record} />;
              }
              return null;
            }}
          />
          <FunctionField
            label="Task"
            render={(record: any) => (
              record?.task ? (
                <RouterLink to={`/tasks/${record.task.id}/show`} style={{ textDecoration: 'none', color: '#3B82F6' }}>
                  {record.task.title}
                </RouterLink>
              ) : '-'
            )}
          />
          <FunctionField
            label="Activity"
            render={(record: any) => record?.activity?.name || '-'}
          />
          <FunctionField
            label="Customer"
            render={(record: any) => record?.customer?.displayName || '-'}
          />
          <FunctionField
            label="Customer Location"
            render={(record: any) => record?.customerLocation?.location?.name || '-'}
          />
          <FunctionField
            label="Equipment"
            render={(record: any) => record?.equipment?.name || '-'}
          />
          <FunctionField
            label="Notes"
            render={(record: any) => record?.notes || '-'}
          />
        </Datagrid>
      </List>
    </Box>
  );
};
